<template>
    <div class="tiptap-editor-wrapper">
        <!-- Toolbar -->
        <div class="editor-toolbar elevation-1 pa-2 d-flex flex-wrap align-center">
            <!-- Variable Inserter -->
            <variable-inserter @insert="insertVariable" />

            <v-divider vertical class="mx-2" />

            <!-- Text Formatting -->
            <v-btn-toggle dense class="mr-2">
                <v-btn small @click="editor.chain().focus().toggleBold().run()" :class="{ 'v-btn--active': editor && editor.isActive('bold') }">
                    <v-icon small>mdi-format-bold</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().toggleItalic().run()" :class="{ 'v-btn--active': editor && editor.isActive('italic') }">
                    <v-icon small>mdi-format-italic</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().toggleUnderline().run()" :class="{ 'v-btn--active': editor && editor.isActive('underline') }">
                    <v-icon small>mdi-format-underline</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().toggleStrike().run()" :class="{ 'v-btn--active': editor && editor.isActive('strike') }">
                    <v-icon small>mdi-format-strikethrough</v-icon>
                </v-btn>
            </v-btn-toggle>

            <!-- Headings -->
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn small outlined class="mr-2" v-bind="attrs" v-on="on">
                        <v-icon small left>mdi-format-header-pound</v-icon>
                        {{ getCurrentHeading }}
                    </v-btn>
                </template>
                <v-list dense>
                    <v-list-item @click="editor.chain().focus().setParagraph().run()">
                        <v-list-item-title>Parágrafo</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-for="level in 6" :key="level" @click="editor.chain().focus().setHeading({ level }).run()">
                        <v-list-item-title>Título {{ level }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>

            <!-- Text Alignment -->
            <v-btn-toggle dense class="mr-2">
                <v-btn small @click="editor.chain().focus().setTextAlign('left').run()" :class="{ 'v-btn--active': editor && editor.isActive({ textAlign: 'left' }) }">
                    <v-icon small>mdi-format-align-left</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().setTextAlign('center').run()" :class="{ 'v-btn--active': editor && editor.isActive({ textAlign: 'center' }) }">
                    <v-icon small>mdi-format-align-center</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().setTextAlign('right').run()" :class="{ 'v-btn--active': editor && editor.isActive({ textAlign: 'right' }) }">
                    <v-icon small>mdi-format-align-right</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().setTextAlign('justify').run()" :class="{ 'v-btn--active': editor && editor.isActive({ textAlign: 'justify' }) }">
                    <v-icon small>mdi-format-align-justify</v-icon>
                </v-btn>
            </v-btn-toggle>

            <!-- Lists -->
            <v-btn-toggle dense class="mr-2">
                <v-btn small @click="editor.chain().focus().toggleBulletList().run()" :class="{ 'v-btn--active': editor && editor.isActive('bulletList') }">
                    <v-icon small>mdi-format-list-bulleted</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().toggleOrderedList().run()" :class="{ 'v-btn--active': editor && editor.isActive('orderedList') }">
                    <v-icon small>mdi-format-list-numbered</v-icon>
                </v-btn>
            </v-btn-toggle>

            <!-- Colors -->
            <v-menu offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn small outlined class="mr-2" v-bind="attrs" v-on="on">
                        <v-icon small left>mdi-format-color-text</v-icon>
                        Cor do Texto
                    </v-btn>
                </template>
                <v-card>
                    <v-card-text class="pa-2">
                        <chrome-picker v-model="textColor" @input="applyTextColor" />
                        <v-btn v-if="hasEyeDropper" small text block class="mt-2" @click="pickTextColor">
                            <v-icon small left>mdi-eyedropper</v-icon>
                            Conta-gotas
                        </v-btn>
                    </v-card-text>
                </v-card>
            </v-menu>

            <!-- Image -->
            <v-btn small outlined class="mr-2" @click="addImage">
                <v-icon small left>mdi-image</v-icon>
                Imagem
            </v-btn>

            <!-- Link -->
            <v-btn small outlined class="mr-2" @click="setLink">
                <v-icon small left>mdi-link</v-icon>
                Link
            </v-btn>

            <!-- Clear Formatting -->
            <v-btn small outlined @click="editor.chain().focus().clearNodes().unsetAllMarks().run()">
                <v-icon small left>mdi-format-clear</v-icon>
                Limpar
            </v-btn>
        </div>

        <!-- Editor Content -->
        <editor-content :editor="editor" class="editor-content pa-4" />

        <!-- Image URL Dialog -->
        <v-dialog v-model="showImageDialog" max-width="500">
            <v-card>
                <v-card-title>Inserir Imagem</v-card-title>
                <v-card-text>
                    <v-tabs v-model="activeTab" class="mb-4">
                        <v-tab>Upload</v-tab>
                        <v-tab>URL</v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="activeTab">
                        <v-tab-item>
                            <v-file-input
                                v-model="imageFile"
                                label="Selecionar imagem"
                                accept="image/*"
                                prepend-icon="mdi-camera"
                                outlined
                                dense
                                @change="handleImageUpload"
                                :loading="uploadingImage"
                            ></v-file-input>
                            <p class="text-caption grey--text">
                                Formatos aceitos: JPEG, PNG, GIF. Máx 2MB.
                            </p>
                        </v-tab-item>
                        <v-tab-item>
                            <v-text-field
                                v-model="imageUrl"
                                label="URL da imagem"
                                placeholder="https://exemplo.com/imagem.jpg"
                                outlined
                                dense
                            />
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="showImageDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" @click="insertImage">Inserir</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Link Dialog -->
        <v-dialog v-model="showLinkDialog" max-width="500">
            <v-card>
                <v-card-title>Inserir Link</v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="linkUrl"
                        label="URL"
                        placeholder="https://exemplo.com"
                        outlined
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="showLinkDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" @click="insertLink">Inserir</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { Chrome } from 'vue-color';
import VariableInserter from './VariableInserter.vue';
import { mapActions } from 'vuex';

export default {
    name: 'TipTapEditor',
    components: {
        EditorContent,
        'chrome-picker': Chrome,
        VariableInserter
    },
    props: {
        value: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editor: null,
            textColor: { hex: '#000000' },
            hasEyeDropper: 'EyeDropper' in window,
            showImageDialog: false,
            imageUrl: '',
            imageFile: null,
            uploadingImage: false,
            activeTab: 0,
            showLinkDialog: false,
            linkUrl: ''
        };
    },
    computed: {
        getCurrentHeading() {
            if (!this.editor) return 'Parágrafo';
            for (let level = 1; level <= 6; level++) {
                if (this.editor.isActive('heading', { level })) {
                    return `Título ${level}`;
                }
            }
            return 'Parágrafo';
        }
    },
    watch: {
        value(newValue) {
            const isSame = this.editor.getHTML() === newValue;
            if (isSame) return;
            this.editor.commands.setContent(newValue, false);
        },
        disabled(newValue) {
            this.editor.setEditable(!newValue);
        }
    },
    mounted() {
        this.editor = new Editor({
            extensions: [
                StarterKit,
                Underline,
                TextAlign.configure({
                    types: ['heading', 'paragraph']
                }),
                Image,
                Link.configure({
                    openOnClick: false
                }),
                TextStyle,
                Color
            ],
            content: this.value,
            editable: !this.disabled,
            onUpdate: () => {
                this.$emit('input', this.editor.getHTML());
            }
        });
    },
    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy();
        }
    },
    methods: {
        ...mapActions('contractTemplates', ['ActionDoUploadContractImage']),
        insertVariable(variableText) {
            if (this.editor) {
                this.editor.chain().focus().insertContent(variableText).run();
            }
        },
        applyTextColor(color) {
            if (this.editor && color.hex) {
                this.editor.chain().focus().setColor(color.hex).run();
            }
        },
        async pickTextColor() {
            if (!this.hasEyeDropper) return;
            
            try {
                const eyeDropper = new window.EyeDropper();
                const result = await eyeDropper.open();
                this.textColor = { hex: result.sRGBHex };
                this.applyTextColor(this.textColor);
            } catch (error) {
                console.error('Eyedropper error:', error);
            }
        },
        addImage() {
            this.imageUrl = '';
            this.imageFile = null;
            this.showImageDialog = true;
        },
        async handleImageUpload(file) {
            if (!file) {
                this.imageFile = null;
                return;
            }
            this.imageFile = file;
        },
        async insertImage() {
            if (this.imageFile) {
                // Upload file
                this.uploadingImage = true;
                try {
                    const formData = new FormData();
                    formData.append('image', this.imageFile);
                    
                    const response = await this.ActionDoUploadContractImage(formData);
                    if (response.data && response.data.url) {
                         this.editor.chain().focus().setImage({ src: response.data.url }).run();
                         this.showImageDialog = false;
                         this.imageUrl = '';
                         this.imageFile = null;
                    }
                } catch (error) {
                    console.error("Error uploading image:", error);
                    // Fallback to URL if upload fails (or handle error UI)
                } finally {
                    this.uploadingImage = false;
                }
            } else if (this.imageUrl && this.editor) {
                this.editor.chain().focus().setImage({ src: this.imageUrl }).run();
                this.showImageDialog = false;
                this.imageUrl = '';
            }
        },
        setLink() {
            const previousUrl = this.editor.getAttributes('link').href;
            this.linkUrl = previousUrl || '';
            this.showLinkDialog = true;
        },
        insertLink() {
            if (this.linkUrl && this.editor) {
                this.editor.chain().focus().setLink({ href: this.linkUrl }).run();
                this.showLinkDialog = false;
                this.linkUrl = '';
            }
        }
    }
};
</script>

<style scoped>
.tiptap-editor-wrapper {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
}

.editor-toolbar {
    background-color: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    gap: 8px;
}

.editor-content {
    min-height: 400px;
    max-height: 600px;
    overflow-y: auto;
    background-color: white;
}

.editor-content >>> .ProseMirror {
    outline: none;
    min-height: 400px;
}

.editor-content >>> .ProseMirror p {
    margin: 0 0 1em 0;
}

.editor-content >>> .ProseMirror h1,
.editor-content >>> .ProseMirror h2,
.editor-content >>> .ProseMirror h3,
.editor-content >>> .ProseMirror h4,
.editor-content >>> .ProseMirror h5,
.editor-content >>> .ProseMirror h6 {
    margin: 1em 0 0.5em 0;
    font-weight: bold;
}

.editor-content >>> .ProseMirror h1 {
    font-size: 2em;
}

.editor-content >>> .ProseMirror h2 {
    font-size: 1.5em;
}

.editor-content >>> .ProseMirror h3 {
    font-size: 1.17em;
}

.editor-content >>> .ProseMirror h4 {
    font-size: 1em;
}

.editor-content >>> .ProseMirror h5 {
    font-size: 0.83em;
}

.editor-content >>> .ProseMirror h6 {
    font-size: 0.67em;
}

.editor-content >>> .ProseMirror img {
    max-width: 100%;
    height: auto;
}

.editor-content >>> .ProseMirror ul,
.editor-content >>> .ProseMirror ol {
    padding-left: 1.5em;
}

.editor-content >>> .ProseMirror a {
    color: #1976d2;
    text-decoration: underline;
}
</style>
